<script>
	import StudentSearchComponent from './StudentSearchComponent.svelte';
	import NavBarReusable from './NavbarReusable.svelte';
	import { onMount } from 'svelte';
	import { getApiBase } from './apiBase.js';

	// Admin tools
	import AdminCreateUserComponent from './AdminCreateUserComponent.svelte';
	import AdminManualPaymentComponent from './AdminManualPaymentComponent.svelte';
	import RefreshDatabaseButtonComponent from './RefreshDatabaseButtonComponent.svelte';
	import CreateStudentEmailComponent from './CreateStudentEmailComponent.svelte';
	import SelectUsersEmailComponent from './SelectUsersEmailComponent.svelte';
	import SendWeeklyScheduleSMS from './SendWeeklyScheduleSMS.svelte';
	import AdminFamilyManagerComponent from './AdminFamilyManagerComponent.svelte';

	// Student widgets
	import StudentInfoComponent from './StudentInfoComponent.svelte';
	import StudentPricingModifierComponent from './StudentPricingModifierComponent.svelte';
	import FindStudentsComments from './FindStudentsComments.svelte';
	import AdminStudentScheduleViewer from './AdminStudentScheduleViewer.svelte';
	import AdminUserInvoicePageViewer from './AdminUserInvoicePageViewer.svelte';

	let selectedStudent = null;

	let infoRefresh = 0;
	let pricingRefresh = 0;
	let commentsRefresh = 0;
	let scheduleRefresh = 0;
	let invoiceRefresh = 0;

	const lastStudentKey = 'adminStudents:lastStudent';
	const collapsedKey = 'adminStudents:collapsed';
	const toolsCollapsedKey = 'adminStudents:collapsedTools';
	const showToolsKey = 'adminStudents:showTools';

	let showAdminTools = true;

	let collapsed = {
		info: false,
		pricing: true,
		comments: true,
		schedule: true,
		invoice: true,
	};

	const widgetOrder = /** @type {const} */ (['info', 'pricing', 'comments', 'schedule', 'invoice']);

	function widgetTitle(key) {
		switch (key) {
			case 'info':
				return 'Student Information';
			case 'pricing':
				return 'Student Pricing';
			case 'comments':
				return 'Student Comments';
			case 'schedule':
				return 'Schedule Debug';
			case 'invoice':
				return '👁️ User Invoice View';
			default:
				return key;
		}
	}

	function normalizeCollapsedState() {
		// Ensure collapsed has all expected keys (no single-open enforcement).
		collapsed = {
			info: !!collapsed?.info,
			pricing: !!collapsed?.pricing,
			comments: !!collapsed?.comments,
			schedule: !!collapsed?.schedule,
			invoice: !!collapsed?.invoice,
		};
		saveCollapsedState();
	}

	$: openWidgetKeys = widgetOrder.filter(k => !collapsed[k]);

	let toolsCollapsed = {
		createUsers: false,
		manualPayments: false,
		refreshDb: false,
		classEmails: false,
		bulkEmails: false,
		sms: false,
		families: false,
	};

	let toast = '';
	let toastTimer = null;

	function showToast(message) {
		toast = message;
		if (toastTimer) clearTimeout(toastTimer);
		toastTimer = setTimeout(() => {
			toast = '';
		}, 1200);
	}

	function studentLabel(student) {
		if (!student) return '';
		const first = (student.first_name || '').trim();
		const last = (student.last_name || '').trim();
		const full = `${first} ${last}`.trim();
		return full || `ID ${student.id}`;
	}

	function getHashParts() {
		const hash = window.location.hash || '#/admin/students';
		const qIndex = hash.indexOf('?');
		if (qIndex === -1) return { path: hash, params: new URLSearchParams() };
		return { path: hash.slice(0, qIndex), params: new URLSearchParams(hash.slice(qIndex + 1)) };
	}

	function getHashQueryParam(name) {
		const { params } = getHashParts();
		return params.get(name);
	}

	function setStudentsHashStudentId(id) {
		const { path, params } = getHashParts();
		// Only mutate when we're on Students page route
		if (!path.startsWith('#/admin/students')) return;
		if (id) params.set('studentId', String(id));
		else params.delete('studentId');
		const nextHash = params.toString() ? `${path}?${params.toString()}` : path;
		if (nextHash !== window.location.hash) window.location.hash = nextHash;
	}

	function persistSelectedStudent(student) {
		try {
			if (!student) {
				localStorage.removeItem(lastStudentKey);
				return;
			}
			localStorage.setItem(lastStudentKey, JSON.stringify(student));
		} catch {
			// ignore
		}
	}

	function loadCollapsedState() {
		try {
			const raw = localStorage.getItem(collapsedKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') return;
			collapsed = {
				info: !!parsed.info,
				pricing: !!parsed.pricing,
				comments: !!parsed.comments,
				schedule: !!parsed.schedule,
				invoice: !!parsed.invoice,
			};
		} catch {
			// ignore
		}
	}

	function saveCollapsedState() {
		try {
			localStorage.setItem(collapsedKey, JSON.stringify(collapsed));
		} catch {
			// ignore
		}
	}

	function openWidget(key) {
		// Multi-open behavior: opening a widget does not close others.
		collapsed = { ...collapsed, [key]: false };
		saveCollapsedState();
	}

	function closeWidget(key) {
		if (!key) return;
		collapsed = { ...collapsed, [key]: true };
		saveCollapsedState();
	}

	function toggleCollapsed(key) {
		// Multi-open behavior: toggle only this widget.
		collapsed = { ...collapsed, [key]: !collapsed[key] };
		saveCollapsedState();
	}

	function loadToolsCollapsedState() {
		try {
			const raw = localStorage.getItem(toolsCollapsedKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') return;
			toolsCollapsed = {
				createUsers: !!parsed.createUsers,
				manualPayments: !!parsed.manualPayments,
				refreshDb: !!parsed.refreshDb,
				classEmails: !!parsed.classEmails,
				bulkEmails: !!parsed.bulkEmails,
				sms: !!parsed.sms,
				families: !!parsed.families,
			};
		} catch {
			// ignore
		}
	}

	function saveToolsCollapsedState() {
		try {
			localStorage.setItem(toolsCollapsedKey, JSON.stringify(toolsCollapsed));
		} catch {
			// ignore
		}
	}

	function toggleToolsCollapsed(key) {
		toolsCollapsed = { ...toolsCollapsed, [key]: !toolsCollapsed[key] };
		saveToolsCollapsedState();
	}

	function closeAllWidgets() {
		collapsed = {
			info: true,
			pricing: true,
			comments: true,
			schedule: true,
			invoice: true,
		};
		saveCollapsedState();
	}

	function loadShowToolsState() {
		try {
			const raw = localStorage.getItem(showToolsKey);
			if (raw === null) return;
			showAdminTools = raw === 'true';
		} catch {
			// ignore
		}
	}

	function toggleTools() {
		showAdminTools = !showAdminTools;
		try {
			localStorage.setItem(showToolsKey, String(showAdminTools));
		} catch {
			// ignore
		}
	}

	async function copyToClipboard(text, label) {
		if (!text) return;
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
			} else {
				const textarea = document.createElement('textarea');
				textarea.value = text;
				textarea.style.position = 'fixed';
				textarea.style.left = '-9999px';
				document.body.appendChild(textarea);
				textarea.focus();
				textarea.select();
				document.execCommand('copy');
				document.body.removeChild(textarea);
			}
			showToast(`Copied ${label}`);
		} catch {
			showToast('Copy failed');
		}
	}

	function jumpTo(key) {
		// Previous behavior was scrolling to a widget section.
		// With the dock model, we open the widget instead.
		openWidget(key);
	}

	async function searchStudents(term) {
		const apiBase = getApiBase();
		const response = await fetch(`${apiBase}/studentplayground/api/admin/search-students/`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
			},
			body: JSON.stringify({ search_term: term }),
		});

		if (!response.ok) return [];
		const data = await response.json();
		return data?.students || [];
	}

	async function restoreSelectedStudent() {
		// 1) deep link: #/admin/students?studentId=123
		const fromHash = getHashQueryParam('studentId');
		if (fromHash) {
			const idNum = parseInt(fromHash, 10);
			if (!Number.isNaN(idNum)) {
				try {
					const results = await searchStudents(String(idNum));
					const match = results.find(s => s.id === idNum) || results[0];
					if (match) {
						selectedStudent = match;
						infoRefresh += 1;
						pricingRefresh += 1;
						commentsRefresh += 1;
						scheduleRefresh += 1;
						invoiceRefresh += 1;
						persistSelectedStudent(match);
						setStudentsHashStudentId(match.id);
						return;
					}
				} catch {
					// ignore
				}
			}
		}

		// 2) last selection persistence
		try {
			const raw = localStorage.getItem(lastStudentKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			if (parsed && parsed.id) {
				selectedStudent = parsed;
				infoRefresh += 1;
				pricingRefresh += 1;
				commentsRefresh += 1;
				scheduleRefresh += 1;
				invoiceRefresh += 1;
				setStudentsHashStudentId(parsed.id);
			}
		} catch {
			// ignore
		}
	}

	function handleStudentSelected(e) {
		selectedStudent = e.detail;
		infoRefresh += 1;
		pricingRefresh += 1;
		commentsRefresh += 1;
		scheduleRefresh += 1;
		invoiceRefresh += 1;
		persistSelectedStudent(selectedStudent);
		setStudentsHashStudentId(selectedStudent?.id);
	}

	function handleStudentCleared() {
		selectedStudent = null;
		persistSelectedStudent(null);
		setStudentsHashStudentId(null);
	}

	onMount(() => {
		loadShowToolsState();
		loadCollapsedState();
		normalizeCollapsedState();
		loadToolsCollapsedState();
		restoreSelectedStudent();
	});
</script>

<div class="app-container">
	<NavBarReusable label="Admin - Students" />

	<div class="page">
		<section class="section">
			<div class="section-title-row">
				<h2 class="section-title">Admin tools</h2>
				<button type="button" class="btn small" on:click={toggleTools}>
					{showAdminTools ? 'Hide tools' : 'Show tools'}
				</button>
			</div>
			{#if showAdminTools}
				<div class="tools-grid">
					<div class="card tool tool-create-users">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-createUsers-body"
								aria-expanded={!toolsCollapsed.createUsers}
								on:click={() => toggleToolsCollapsed('createUsers')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.createUsers ? '▸' : '▾'}</span>
								Create Users
							</button>
						</div>
						{#if !toolsCollapsed.createUsers}
							<div class="widget-body" id="students-tool-createUsers-body">
								<AdminCreateUserComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-manual-payments">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-manualPayments-body"
								aria-expanded={!toolsCollapsed.manualPayments}
								on:click={() => toggleToolsCollapsed('manualPayments')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.manualPayments ? '▸' : '▾'}</span>
								Manual Payments
							</button>
						</div>
						{#if !toolsCollapsed.manualPayments}
							<div class="widget-body" id="students-tool-manualPayments-body">
								<AdminManualPaymentComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-db-refresh">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-refreshDb-body"
								aria-expanded={!toolsCollapsed.refreshDb}
								on:click={() => toggleToolsCollapsed('refreshDb')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.refreshDb ? '▸' : '▾'}</span>
								Database refresh
							</button>
						</div>
						{#if !toolsCollapsed.refreshDb}
							<div class="widget-body" id="students-tool-refreshDb-body">
								<RefreshDatabaseButtonComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-class-emails">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-classEmails-body"
								aria-expanded={!toolsCollapsed.classEmails}
								on:click={() => toggleToolsCollapsed('classEmails')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.classEmails ? '▸' : '▾'}</span>
								Send Class Emails
							</button>
						</div>
						{#if !toolsCollapsed.classEmails}
							<div class="widget-body" id="students-tool-classEmails-body">
								<CreateStudentEmailComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-bulk-emails">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-bulkEmails-body"
								aria-expanded={!toolsCollapsed.bulkEmails}
								on:click={() => toggleToolsCollapsed('bulkEmails')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.bulkEmails ? '▸' : '▾'}</span>
								Send Bulk Emails
							</button>
						</div>
						{#if !toolsCollapsed.bulkEmails}
							<div class="widget-body" id="students-tool-bulkEmails-body">
								<SelectUsersEmailComponent />
							</div>
						{/if}
					</div>

					<div class="card tool tool-sms">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-sms-body"
								aria-expanded={!toolsCollapsed.sms}
								on:click={() => toggleToolsCollapsed('sms')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.sms ? '▸' : '▾'}</span>
								Send SMS
							</button>
						</div>
						{#if !toolsCollapsed.sms}
							<div class="widget-body" id="students-tool-sms-body">
								<SendWeeklyScheduleSMS />
							</div>
						{/if}
					</div>

					<div class="card tool tool-families">
						<div class="widget-header">
							<button
								type="button"
								class="widget-title"
								aria-controls="students-tool-families-body"
								aria-expanded={!toolsCollapsed.families}
								on:click={() => toggleToolsCollapsed('families')}
							>
								<span class="chevron" aria-hidden="true">{toolsCollapsed.families ? '▸' : '▾'}</span>
								👪 Families
							</button>
						</div>
						{#if !toolsCollapsed.families}
							<div class="widget-body" id="students-tool-families-body">
								<AdminFamilyManagerComponent />
							</div>
						{/if}
					</div>
				</div>
			{/if}
		</section>

		<section class="section">
			<h2 class="section-title">Find student</h2>
			<StudentSearchComponent
				{selectedStudent}
				on:studentSelected={handleStudentSelected}
				on:studentCleared={handleStudentCleared}
			/>
		</section>

		<section class="section">
			<h2 class="section-title">Student dashboard</h2>

			{#if !selectedStudent}
				<div class="empty">
					Select a student to load comments, pricing, schedule, invoices, and student info.
				</div>
			{:else}
				<div class="selected-row">
					<div class="selected-meta">
						<strong>{studentLabel(selectedStudent)}</strong>
						{#if selectedStudent.username || selectedStudent.email}
							<span class="muted">• @{selectedStudent.username} • {selectedStudent.email}</span>
						{:else}
							<span class="muted">• ID: {selectedStudent.id}</span>
						{/if}

						<div class="quick-actions">
							<button type="button" class="btn small" on:click={() => copyToClipboard(String(selectedStudent.id), 'ID')}>Copy ID</button>
							<button type="button" class="btn small" disabled={!selectedStudent.email} on:click={() => copyToClipboard(selectedStudent.email, 'email')}>Copy email</button>
							<button type="button" class="btn small" disabled={!selectedStudent.phone_number} on:click={() => copyToClipboard(selectedStudent.phone_number, 'phone')}>Copy phone</button>
							<button type="button" class="btn small" disabled={!selectedStudent.username} on:click={() => copyToClipboard(selectedStudent.username, 'username')}>Copy username</button>
							<button type="button" class="btn small" on:click={() => openWidget('info')}>Info</button>
							<button type="button" class="btn small" on:click={() => openWidget('pricing')}>Pricing</button>
							<button type="button" class="btn small" on:click={() => openWidget('comments')}>Comments</button>
							<button type="button" class="btn small" on:click={() => openWidget('schedule')}>Schedule</button>
							<button type="button" class="btn small" on:click={() => openWidget('invoice')}>Invoice</button>
						</div>
					</div>
					<div class="selected-actions">
						<button type="button" class="btn small" disabled={openWidgetKeys.length === 0} on:click={closeAllWidgets}>
							Close all widgets
						</button>
						<button type="button" class="btn" on:click={handleStudentCleared}>Clear</button>
					</div>
				</div>
				{#if toast}
					<div class="toast" role="status">{toast}</div>
				{/if}

				{#if widgetOrder.some(k => collapsed[k])}
					<div class="widget-dock" aria-label="Closed widgets">
						<div class="widget-dock-label">Closed widgets</div>
						<div class="widget-dock-buttons">
							{#each widgetOrder as key (key)}
								{#if collapsed[key]}
									<button type="button" class="dock-btn" on:click={() => openWidget(key)}>
										{widgetTitle(key)}
									</button>
								{/if}
							{/each}
						</div>
					</div>
				{/if}

				<div class="widgets-grid">
					{#if openWidgetKeys.length === 0}
						<div class="empty widgets-empty">Select a widget above to open.</div>
					{:else}
						{#each openWidgetKeys as key (key)}
							{#if key === 'info'}
								<div class="card widget info-card" id="students-widget-info">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="students-widget-info-body"
											aria-expanded={!collapsed.info}
											on:click={() => toggleCollapsed('info')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.info ? '▸' : '▾'}</span>
											Student Information
										</button>
										<div class="widget-actions">
											<button type="button" class="btn small" on:click={() => (infoRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('info')}>Close</button>
										</div>
									</div>
									<div class="widget-body" id="students-widget-info-body">
										{#key infoRefresh}
											<StudentInfoComponent studentId={selectedStudent.id} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}

							{#if key === 'pricing'}
								<div class="card widget pricing-card" id="students-widget-pricing">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="students-widget-pricing-body"
											aria-expanded={!collapsed.pricing}
											on:click={() => toggleCollapsed('pricing')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.pricing ? '▸' : '▾'}</span>
											Student Pricing
										</button>
										<div class="widget-actions">
											<button type="button" class="btn small" on:click={() => (pricingRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('pricing')}>Close</button>
										</div>
									</div>
									<div class="widget-body" id="students-widget-pricing-body">
										{#key pricingRefresh}
											<StudentPricingModifierComponent studentId={selectedStudent.id} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}

							{#if key === 'comments'}
								<div class="card widget comments-card" id="students-widget-comments">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="students-widget-comments-body"
											aria-expanded={!collapsed.comments}
											on:click={() => toggleCollapsed('comments')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.comments ? '▸' : '▾'}</span>
											Student Comments
										</button>
										<div class="widget-actions">
											<button type="button" class="btn small" on:click={() => (commentsRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('comments')}>Close</button>
										</div>
									</div>
									<div class="widget-body scroll" id="students-widget-comments-body">
										{#key commentsRefresh}
											<FindStudentsComments studentId={selectedStudent.id} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}

							{#if key === 'schedule'}
								<div class="card widget schedule-card" id="students-widget-schedule">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="students-widget-schedule-body"
											aria-expanded={!collapsed.schedule}
											on:click={() => toggleCollapsed('schedule')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.schedule ? '▸' : '▾'}</span>
											Schedule Debug
										</button>
										<div class="widget-actions">
											<a class="link" href={`#/admin/student-schedule-debug?studentId=${selectedStudent.id}`} target="_blank" rel="noreferrer">Open full</a>
											<button type="button" class="btn small" on:click={() => (scheduleRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('schedule')}>Close</button>
										</div>
									</div>
									<div class="widget-body scroll" id="students-widget-schedule-body">
										{#key scheduleRefresh}
											<AdminStudentScheduleViewer studentId={selectedStudent.id} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}

							{#if key === 'invoice'}
								<div class="card widget invoice-card" id="students-widget-invoice">
									<div class="widget-header">
										<button
											type="button"
											class="widget-title"
											aria-controls="students-widget-invoice-body"
											aria-expanded={!collapsed.invoice}
											on:click={() => toggleCollapsed('invoice')}
										>
											<span class="chevron" aria-hidden="true">{collapsed.invoice ? '▸' : '▾'}</span>
											👁️ User Invoice View
										</button>
										<div class="widget-actions">
											<a class="link" href={`#/admin/user-invoice-view?studentId=${selectedStudent.id}`} target="_blank" rel="noreferrer">Open full</a>
											<button type="button" class="btn small" on:click={() => (invoiceRefresh += 1)}>Reload</button>
											<button type="button" class="btn small" on:click={() => closeWidget('invoice')}>Close</button>
										</div>
									</div>
									<div class="widget-body scroll" id="students-widget-invoice-body">
										{#key invoiceRefresh}
											<AdminUserInvoicePageViewer studentId={selectedStudent.id} embedded={true} />
										{/key}
									</div>
								</div>
							{/if}
						{/each}
					{/if}
				</div>
			{/if}
		</section>
	</div>
</div>

<style>
	.page {
		padding: 18px 22px 30px;
		background: #f6f7fb;
	}

	.section {
		margin-bottom: 22px;
	}

	.section-title {
		margin: 0 0 10px 0;
		font-size: 18px;
	}

	.section-title-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin: 0 0 10px 0;
	}

	.tools-grid {
		/* Masonry-style layout to avoid large empty gaps when some cards are collapsed */
		column-count: 3;
		column-gap: 24px;
	}

	/* Cards inside a column layout */
	.tools-grid > .card {
		break-inside: avoid;
		/* keep card's flex layout, but prevent horizontal overlap */
		display: flex;
		width: 100%;
		box-sizing: border-box;
		margin: 0 0 16px;
	}

	.widgets-grid {
		/* Match tools-grid stacking behavior (masonry-style columns). */
		column-count: 2;
		column-gap: 16px;
	}

	.widgets-grid > .card {
		break-inside: avoid;
		display: flex;
		width: 100%;
		box-sizing: border-box;
		margin: 0 0 16px;
	}

	.widget-dock {
		margin: 10px 0 14px;
		padding: 12px;
		border-radius: 10px;
		background: rgba(255, 255, 255, 0.7);
		border: 1px solid rgba(0, 0, 0, 0.08);
	}

	.widget-dock-label {
		font-weight: 800;
		color: #1f2937;
		margin-bottom: 8px;
	}

	.widget-dock-buttons {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.dock-btn {
		padding: 6px 10px;
		border: 1px solid rgba(0, 0, 0, 0.14);
		background: rgba(255, 255, 255, 0.95);
		border-radius: 999px;
		cursor: pointer;
		font-size: 12px;
		font-weight: 700;
		color: #111827;
	}

	.dock-btn:hover {
		background: white;
	}

	.widgets-empty {
		/* Ensure the empty state spans full width in column layout */
		column-span: all;
	}

	.card {
		padding: 15px;
		background-color: white;
		display: flex;
		flex-direction: column;
		gap: 10px;
		box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
		border-radius: 8px;
		color: black;
		align-items: stretch;
	}

	/* Widget theming: makes boundaries obvious */
	.card.widget {
		background: var(--tint, #fff);
		border-left: 6px solid var(--accent, transparent);
	}

	/* Tool theming: same visual separation as widgets */
	.card.tool {
		background: var(--tint, #fff);
		border-left: 6px solid var(--accent, transparent);
	}

	.tool-create-users {
		--accent: #0ea5e9;
		--tint: #e0f2fe;
	}
	.tool-manual-payments {
		--accent: #6366f1;
		--tint: #eef2ff;
	}
	.tool-db-refresh {
		--accent: #6b7280;
		--tint: #f3f4f6;
	}
	.tool-class-emails {
		--accent: #14b8a6;
		--tint: #ccfbf1;
	}
	.tool-bulk-emails {
		--accent: #a855f7;
		--tint: #f5f3ff;
	}
	.tool-sms {
		--accent: #f97316;
		--tint: #ffedd5;
	}
	.tool-families {
		--accent: #14695b;
		--tint: #e6f4f1;
	}

	.info-card {
		--accent: #2563eb;
		--tint: #eff6ff;
	}
	.pricing-card {
		--accent: #7c3aed;
		--tint: #f5f3ff;
	}
	.comments-card {
		--accent: #d97706;
		--tint: #fffbeb;
	}
	.schedule-card {
		--accent: #059669;
		--tint: #ecfdf5;
	}
	.invoice-card {
		--accent: #dc2626;
		--tint: #fef2f2;
	}

	.widget-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
	}

	.widget-actions {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.widget-title {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		padding: 6px 8px;
		border: 0;
		background: transparent;
		font-weight: 800;
		color: #1f2937;
		cursor: pointer;
		border-radius: 8px;
		text-align: left;
	}

	.widget-title:hover {
		background: rgba(255, 255, 255, 0.7);
	}

	.widget-title:focus {
		outline: 2px solid rgba(20, 105, 91, 0.4);
		outline-offset: 2px;
	}

	.chevron {
		font-size: 14px;
		opacity: 0.9;
	}

	.widget-body {
		width: 100%;
	}

	.widget-body.scroll {
		max-height: 650px;
		overflow: auto;
		padding: 10px;
		border-radius: 8px;
		border: 1px solid rgba(0, 0, 0, 0.08);
		background: rgba(255, 255, 255, 0.75);
	}

	.selected-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		margin: 10px 0 14px;
		padding: 10px 12px;
		border-radius: 8px;
		background: rgba(248, 249, 250, 0.92);
		backdrop-filter: blur(8px);
		border: 1px solid rgba(0, 0, 0, 0.08);
		position: sticky;
		top: 10px;
		z-index: 20;
	}

	.quick-actions {
		margin-top: 8px;
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.toast {
		margin: 10px 0 14px;
		padding: 10px 12px;
		border-radius: 8px;
		background: #e7f3ff;
		border: 1px solid #bcdcff;
		color: #0b3d69;
	}

	.muted {
		color: #666;
		font-weight: 400;
	}

	.btn {
		padding: 8px 12px;
		border: 1px solid #ccc;
		background: white;
		border-radius: 8px;
		cursor: pointer;
		white-space: nowrap;
	}

	.btn.small {
		padding: 6px 10px;
		border-radius: 6px;
		font-size: 12px;
	}

	.link {
		color: #14695b;
		text-decoration: underline;
		font-size: 12px;
		white-space: nowrap;
	}

	.empty {
		padding: 18px 16px;
		border-radius: 8px;
		background: #f8f9fa;
		border: 1px solid #e0e0e0;
		color: #666;
	}

	@media (max-width: 1400px) {
		.tools-grid {
			column-count: 2;
		}
	}

	@media (max-width: 1100px) {
		.tools-grid {
			column-count: 1;
		}

		.widgets-grid {
			column-count: 1;
		}
	}
</style>

